import { useState } from "react";
import { ChevronDown, ChevronUp, Share2, Globe, Info } from "lucide-react";
import ImagePicker from "@/components/ImagePicker";

/**
 * Per-page SEO editor — surfaces seo_title, seo_description, cover_image
 * and shows a live "how it looks when shared" preview card.
 *
 * Props:
 *  - page: { title, slug, seo_title, seo_description, cover_image }
 *  - onChange: (patch) => void   // called on blur — parent triggers save
 *  - siteHost: string            // e.g. "videoepreneur.com" — used for the preview URL line
 */
export default function SeoPanel({ page, onChange, siteHost = "videoepreneur.com" }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({
    seo_title: page.seo_title || "",
    seo_description: page.seo_description || "",
    cover_image: page.cover_image || "",
  });

  const patch = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const blurSave = (k) => {
    if ((page[k] || "") !== (draft[k] || "")) onChange({ [k]: draft[k] });
  };
  const saveImage = (v) => {
    setDraft((d) => ({ ...d, cover_image: v }));
    onChange({ cover_image: v });
  };

  // Effective values used in the live share preview
  const previewTitle = (draft.seo_title || page.title || "Untitled page").slice(0, 65);
  const previewDesc = (draft.seo_description || `Join ${page.title || ""} — hosted on Videoepreneur.`).slice(0, 155);
  const previewImage = draft.cover_image || "";
  const previewUrl = `${siteHost}/p/${page.slug || "your-slug"}`;

  return (
    <div className="vp-card overflow-hidden" data-testid="seo-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 p-4 hover:bg-[#F1F3F7]"
        data-testid="seo-panel-toggle"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-[#0B5CFF]/10 flex items-center justify-center">
            <Share2 className="w-4 h-4 text-[#0B5CFF]" strokeWidth={1.75} />
          </div>
          <div className="text-left">
            <div className="font-display font-semibold text-sm sm:text-base">SEO & Social share preview</div>
            <div className="text-xs text-[#747680]">How your page looks on Google, WhatsApp, Twitter, LinkedIn.</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-[#4A4D57]" /> : <ChevronDown className="w-4 h-4 text-[#4A4D57]" />}
      </button>

      {open && (
        <div className="border-t border-[#E4E6EC] p-4 sm:p-5 space-y-5">
          <div className="grid lg:grid-cols-2 gap-5">
            {/* ---- FORM ---- */}
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="vp-label" style={{ fontSize: "0.65rem" }}>SEO title (page + tab title)</span>
                  <span className={"text-[10px] " + (draft.seo_title.length > 60 ? "text-[#B00020]" : "text-[#747680]")}>{draft.seo_title.length}/60 chars</span>
                </div>
                <input
                  className="vp-input text-sm"
                  value={draft.seo_title}
                  onChange={(e) => patch("seo_title", e.target.value)}
                  onBlur={() => blurSave("seo_title")}
                  placeholder="e.g. Chandan Mishra Live Workshop — Videoepreneur"
                  data-testid="seo-title-input"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="vp-label" style={{ fontSize: "0.65rem" }}>SEO description</span>
                  <span className={"text-[10px] " + (draft.seo_description.length > 155 ? "text-[#B00020]" : "text-[#747680]")}>{draft.seo_description.length}/155 chars</span>
                </div>
                <textarea
                  rows={3}
                  className="vp-input text-sm"
                  value={draft.seo_description}
                  onChange={(e) => patch("seo_description", e.target.value)}
                  onBlur={() => blurSave("seo_description")}
                  placeholder="1-2 sentence summary shown on search results and social previews."
                  data-testid="seo-description-input"
                />
              </div>

              <div>
                <div className="vp-label mb-1.5" style={{ fontSize: "0.65rem" }}>Cover image (1200×630 recommended)</div>
                <ImagePicker
                  label="Social preview image"
                  value={draft.cover_image}
                  onChange={saveImage}
                  testid="seo-cover-image"
                />
                <div className="text-[10px] text-[#747680] mt-1.5 flex items-start gap-1.5">
                  <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>This image shows on WhatsApp, Twitter and LinkedIn when someone shares your page link. 1200×630 gives the sharpest preview.</span>
                </div>
              </div>
            </div>

            {/* ---- LIVE SHARE PREVIEW ---- */}
            <div className="space-y-3">
              <div className="vp-label" style={{ fontSize: "0.65rem" }}>Live preview</div>

              {/* WhatsApp / iMessage-style card */}
              <div className="rounded-2xl overflow-hidden border border-[#E4E6EC] bg-white shadow-sm" data-testid="seo-preview-whatsapp">
                {previewImage ? (
                  <img src={previewImage} alt="cover" className="w-full aspect-[1200/630] object-cover bg-[#F1F3F7]" onError={(e) => { e.target.style.display = "none"; }} />
                ) : (
                  <div className="w-full aspect-[1200/630] bg-gradient-to-br from-[#0B5CFF]/10 to-[#0B5CFF]/5 flex items-center justify-center text-xs text-[#747680]">
                    Cover image goes here
                  </div>
                )}
                <div className="p-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#747680]">{previewUrl}</div>
                  <div className="font-display font-semibold text-sm mt-1 leading-snug line-clamp-2">{previewTitle}</div>
                  <div className="text-xs text-[#4A4D57] mt-1 leading-snug line-clamp-2">{previewDesc}</div>
                </div>
              </div>
              <div className="text-[10px] text-[#747680] text-center">WhatsApp / iMessage / LinkedIn preview</div>

              {/* Google search result-style card */}
              <div className="rounded-lg border border-[#E4E6EC] bg-white p-3.5" data-testid="seo-preview-google">
                <div className="flex items-center gap-1.5">
                  <div className="w-6 h-6 rounded-full bg-[#0B5CFF]/10 flex items-center justify-center">
                    <Globe className="w-3 h-3 text-[#0B5CFF]" strokeWidth={2} />
                  </div>
                  <div className="text-xs text-[#4A4D57]">
                    <div className="font-medium leading-tight">{siteHost}</div>
                    <div className="text-[10px] text-[#747680] leading-tight">{previewUrl}</div>
                  </div>
                </div>
                <div className="text-[#1A0DAB] font-medium text-base mt-2 leading-snug line-clamp-2 hover:underline cursor-default">{previewTitle}</div>
                <div className="text-xs text-[#4A4D57] mt-0.5 leading-snug line-clamp-2">{previewDesc}</div>
              </div>
              <div className="text-[10px] text-[#747680] text-center">Google search result preview</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
